import type {SyntheticEvent, FC} from 'react'
import cn from 'clsx'
import type {LDFlagSet} from 'launchdarkly-node-server-sdk'
import {useSubmit} from '@remix-run/react'
import {FeedbackStar} from '~/icons/feedback-star'
import type {DomainsBaseStyles, ImprovedHomepageDataProps, RootContext, TrackAnalytics} from '~/types'
import {useReportWidgetClick, useReportWidgetSeen, useSendPartnerProductClickoutCustom} from '~/hooks/analytic-hooks'
import {getSchemaPriceValidUntil, objectToQueryString} from '~/utils/commons'
import {CLICK_ID_PLACEHOLDER} from '~/utils/affiliation'
import {grantAllConsent} from '~/utils/consent-manager.client'

type TopRatedDiscountCardProps = {
  topRatedDiscount: ImprovedHomepageDataProps['topDiscounts'][number]
  domainStyles: DomainsBaseStyles
  buyItOnAmazonLabel: string
  discountBadgeLabel: (discount: number) => string
  formattedRating: string
  cardIndex: number
  useFourColumnLayout?: boolean
  trackingContext: {
    renderTimestamp: number
    featureFlags: LDFlagSet
    hostname: string
    trackAnalytics: TrackAnalytics
    clickId: string
    setShowCookieConsentGdpr: RootContext['setShowCookieConsentGdpr']
    locale: string
    traffic?: string
    currencyCode: string
  }
}

const TopRatedDiscountCard: FC<TopRatedDiscountCardProps> = ({
  topRatedDiscount,
  domainStyles,
  buyItOnAmazonLabel,
  discountBadgeLabel,
  formattedRating,
  cardIndex,
  useFourColumnLayout = false,
  trackingContext,
}) => {
  const {
    renderTimestamp,
    hostname,
    currencyCode,
    featureFlags,
    trackAnalytics,
    clickId,
    setShowCookieConsentGdpr,
    locale,
    traffic,
  } = trackingContext
  const submit = useSubmit()
  const reportWidgetClick = useReportWidgetClick()

  const [cardRef] = useReportWidgetSeen({
    widget_name: 'improved-home-top-rated-discounts-card',
    widget_place: `${cardIndex + 1}`,
  })

  const {discount, title, brand, ratingScore, affiliateLink, image, slug, asin, price} = topRatedDiscount || {}
  const affiliateLinkProps = {
    href: affiliateLink?.url,
    ariaLabel: `view ${title}`,
    target: '_blank',
    rel: 'sponsored noreferrer',
  }

  const showDiscount = Boolean(discount && discount > 0)
  const showRating = Boolean(ratingScore && ratingScore > 0)
  const showBrand = Boolean(brand?.length)
  const showName = Boolean(title?.length)

  const shouldForceTrackAnalytics = Boolean(
    featureFlags?.cookieConsentVariation === 'opt-in' &&
    (!trackAnalytics?.permission || trackAnalytics?.permission === 'denied'),
  )

  const trackSearchParams = objectToQueryString({
    session_id: trackAnalytics?.session_id,
    ...trackAnalytics?.pageLoadData,
    page_id: trackAnalytics?.page_id,
    user_id: trackAnalytics?.user_id,
  })

  const affiliateUrl = affiliateLink?.url ?? ''
  const affiliateLinkWithClickId = affiliateUrl ? affiliateUrl.replaceAll(CLICK_ID_PLACEHOLDER, clickId) : ''

  const redirectUrl = shouldForceTrackAnalytics
    ? `/redirect-analytics?affiliateLink=${encodeURIComponent(affiliateLinkWithClickId)}&${trackSearchParams}`
    : affiliateLinkWithClickId || '#'

  const reportClickout = useSendPartnerProductClickoutCustom()

  const handleCardClick = (event: SyntheticEvent) => {
    if (!affiliateLinkWithClickId) {
      event.preventDefault()
      return
    }

    if (shouldForceTrackAnalytics) {
      setShowCookieConsentGdpr(false)
      grantAllConsent()
      submit(
        {
          'accept-gdpr': 'granted',
          redirect: 'true',
          'track-analytics-data': JSON.stringify({...trackAnalytics, permission: 'granted'}),
        },
        {method: 'POST', action: '/cookie-consent', replace: false, navigate: false},
      )
    }

    reportClickout({
      index: cardIndex + 1,
      item_list_id: slug,
      click_element_name: 'improved-home-top-rated-discounts',
      partner_item_brand: title,
      partner_item_id: asin,
      item_name: title,
      item_brand: brand,
      click_id: affiliateLink?.clickId,
      affiliation: affiliateLink?.type,
      is_dws: false,
      location_id: locale,
      gclid: undefined,
      sys_id: undefined,
      target: affiliateLinkWithClickId,
      traffic: traffic || 'unknown',
    })

    reportWidgetClick({
      widget_name: 'improved-home-top-rated-discounts-card',
      widget_place: `${cardIndex + 1}`,
    })

    if (typeof Storage != 'undefined') {
      localStorage.setItem('clickTime', Date.now().toString())
      if (featureFlags?.showHustonProblemPopup) localStorage.setItem('redirectUrl', redirectUrl)
    }
  }

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org/',
            '@type': 'Product',
            name: title,
            image: [image],
            description: title,
            brand: {
              '@type': 'Brand',
              name: brand,
            },
            offers: {
              '@type': 'Offer',
              url: `https://${hostname}${affiliateLinkWithClickId}`,
              priceCurrency: currencyCode,
              price: price,
              priceValidUntil: getSchemaPriceValidUntil(renderTimestamp, 'en-CA'),
              itemCondition: 'https://schema.org/NewCondition',
              availability: 'https://schema.org/InStock',
              seller: {
                '@type': 'Organization',
                name: brand ?? 'Amazon',
              },
            },
          }),
        }}
      ></script>
      <div
        ref={cardRef}
        className={cn(
          'flex !h-[437px] !min-w-[265px] flex-col justify-between rounded-2xl bg-white p-3.5 shadow-[0px_1.94px_9.68px_0px_#00000026] mobile:!h-[343px] mobile:!min-w-[208px] mobile:rounded-xl',
          useFourColumnLayout && 'desktop:!min-w-[calc((100%-36px)/4)] desktop:!max-w-[calc((100%-36px)/4)]',
        )}
      >
        <a
          href={redirectUrl}
          onClick={handleCardClick}
          aria-label={affiliateLinkProps.ariaLabel}
          target={affiliateLinkProps.target}
          rel={affiliateLinkProps.rel}
          className="relative flex h-[245px] w-full items-center justify-center rounded-[10px] bg-[#E2E8F0] mobile:h-48 mobile:rounded-lg"
        >
          {showDiscount ? (
            <span className="absolute right-2.5 top-2.5 z-10 flex h-[29px] flex-row items-center justify-center rounded-full bg-[#BD4124] px-2.5 text-[15px] font-medium lowercase text-white mobile:right-2 mobile:top-2 mobile:h-[22px] mobile:text-xs mobile:font-normal">
              {discountBadgeLabel(discount)}
            </span>
          ) : null}
          <img
            src={image}
            alt={title}
            className="h-[235px] w-[224px] object-scale-down mix-blend-multiply mobile:h-[182px] mobile:w-[174px]"
          />
        </a>
        <div className="flex flex-col desktop:gap-y-1">
          {showBrand ? (
            <a
              href={redirectUrl}
              onClick={handleCardClick}
              aria-label={affiliateLinkProps.ariaLabel}
              target={affiliateLinkProps.target}
              rel={affiliateLinkProps.rel}
            >
              <span className="mt-2 text-base font-light text-[#828282] mobile:text-[13px]">{brand}</span>
            </a>
          ) : null}
          {showName ? (
            <a
              href={redirectUrl}
              onClick={handleCardClick}
              aria-label={affiliateLinkProps.ariaLabel}
              target={affiliateLinkProps.target}
              rel={affiliateLinkProps.rel}
            >
              <span className="line-clamp-1 text-[23px] font-medium leading-tight text-black mobile:text-[18px]">
                {title}
              </span>
            </a>
          ) : null}
          {showRating ? (
            <a
              href={redirectUrl}
              onClick={handleCardClick}
              aria-label={affiliateLinkProps.ariaLabel}
              target={affiliateLinkProps.target}
              rel={affiliateLinkProps.rel}
              className="flex flex-row items-center gap-2 text-base mobile:gap-1 mobile:text-[13px]"
            >
              <FeedbackStar fill="#FBBF24" className="size-3.5 mobile:size-2.5" />
              {formattedRating}
            </a>
          ) : null}
        </div>
        <a
          href={redirectUrl}
          onClick={handleCardClick}
          aria-label={affiliateLinkProps.ariaLabel}
          target={affiliateLinkProps.target}
          className={cn(
            domainStyles.LISTPAGE_BUTTON_PRIMARY,
            'flex h-[48px] w-full items-center justify-center rounded-[30px] text-[18px] font-light mobile:h-[38px] mobile:text-sm',
            'listpage_button_primary',
          )}
          rel={affiliateLinkProps.rel}
        >
          {buyItOnAmazonLabel}
        </a>
      </div>
    </>
  )
}

export default TopRatedDiscountCard
